import { once } from 'ramda';
import React, { type RefObject, type VFC, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { sendDwhTooltip } from 'reactApp/modules/dwh/dwh.helpers';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import type { EStorageType } from 'reactApp/modules/storage/storage.types';
import { renderFakedoorNeuroOrganizerDialog } from 'reactApp/ui/FakedoorNeuroOrganizePromo/Popup/FakedoorNeuroOrganizerDialog';
import { checkTooltipCanBeShown } from 'reactApp/ui/FakedoorNeuroOrganizePromo/utils';
import { FloatingTooltip } from 'reactApp/ui/FloatingTooltip/FloatingTooltip';
import { ETooltipPlacement, ETooltipSize } from 'reactApp/ui/FloatingTooltip/FloatingTooltip.types';
import { sendXray } from 'reactApp/utils/ga';

import styles from './AiOrganizeTooltip.css';

type TooltipType = 'search' | 'sort';

interface IProps {
    targetRef: RefObject<HTMLDivElement>;
    type?: TooltipType;
}

const name = 'mnz_ai_org_tip_';
const tipSearchId = 'ai-organize-search-tip';
const tipSortId = 'ai-organize-sort-tip';

const sendTipTechRadar = (storage: EStorageType, type: TooltipType, action: 'show' | 'click' | 'close' = 'show') => {
    sendXray([`tip${type}`, 'ai_organize', action, storage]);
};

const sendCloseRadar = once((type, storage) => {
    sendDwhTooltip({ action: 'close', name: name + type, place: storage });
    sendTipTechRadar(storage, type, 'close');
});

const config: Record<TooltipType, string> = {
    search: 'Найдёт нужное без названия — напишите по памяти, что было на фото или в документе',
    sort: 'Нейросеть разложит файлы по папкам и проставит метки. Например: отпуск, отчёты',
};

export const AiOrganizeTooltip: VFC<IProps> = ({ targetRef, type = 'search' }) => {
    const [show, setShow] = useState(false);
    const storage = useSelector(getCurrentStorage);

    useEffect(() => {
        if (targetRef?.current && checkTooltipCanBeShown(type === 'search' ? tipSearchId : tipSortId)) {
            setShow(true);
        }
    }, [targetRef?.current, type]);

    const handleClose = useCallback(() => {
        setShow(false);

        sendCloseRadar(type, storage);
    }, [storage, type]);
    const handleClick = useCallback(() => {
        setShow(false);

        sendDwhTooltip({ action: 'click', name: name + type, place: storage });
        sendTipTechRadar(storage, type, 'click');

        renderFakedoorNeuroOrganizerDialog({ isByClick: true });
    }, [storage, type]);

    const handleShow = useCallback(() => {
        sendTipTechRadar(storage, type);
        sendDwhTooltip({ name: name + type, place: storage });
    }, [storage, type]);

    if (!show) {
        return null;
    }

    return (
        <FloatingTooltip
            size={ETooltipSize.medium}
            buttonText={'Узнать больше'}
            placement={ETooltipPlacement.bottomEnd}
            closeOnOutsideClick={true}
            qaId={`ai-organize-id-${type}`}
            buttonFitContent
            target={targetRef}
            className={styles.root}
            contentClassName={styles.tipContent}
            text={<div className={styles.tipText}>{config[type]}</div>}
            onCrossClose={handleClose}
            onClose={handleClose}
            onClick={handleClick}
            onShow={handleShow}
            autoUpdateOnResize
        />
    );
};

AiOrganizeTooltip.displayName = 'AiOrganizeTooltip';
